import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CheckCircle2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { format } from "date-fns";

const TestSubmissionSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as { 
    testId?: string; 
    testTitle?: string; 
    completedAt?: string;
    employerMessage?: string;
    employerOrganization?: string;
  } | undefined;

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="max-w-2xl w-full border-border">
        <CardContent className="pt-12 pb-8 px-6 md:px-12">
          <div className="flex flex-col items-center text-center space-y-6">
            {/* Success Icon */}
            <div className="rounded-full bg-primary/10 p-6">
              <CheckCircle2 className="h-16 w-16 text-primary" />
            </div>

            {/* Main Message */}
            <div className="space-y-3">
              <h1 className="text-3xl md:text-4xl font-bold text-foreground">
                Thank You!
              </h1>
              <p className="text-lg text-muted-foreground max-w-md">
                Your response has been recorded and is currently being scored.
              </p>
            </div>

            {/* Test Details */}
            {state?.completedAt && (
              <div className="bg-muted/50 rounded-lg p-4 w-full max-w-md space-y-2">
                {state.testTitle && (
                  <p className="text-sm text-foreground">
                    <span className="font-medium">Test:</span> {state.testTitle}
                  </p>
                )}
                <p className="text-sm text-muted-foreground">
                  <span className="font-medium">Submitted:</span>{" "}
                  {format(new Date(state.completedAt), "MMM dd, yyyy 'at' hh:mm a")}
                </p>
              </div>
            )}

            {/* What's Next Section */}
            <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md text-left space-y-3">
              <h2 className="font-semibold text-foreground text-lg">What happens next?</h2>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-0.5">•</span>
                  <span>Your responses are being evaluated</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-0.5">•</span>
                  <span>You'll be notified of next steps via email</span>
                </li>
              </ul>
            </div>

            {/* Employer Custom Message */}
            {state?.employerMessage && (
              <>
                <Separator className="my-4" />
                <div className="bg-muted/50 rounded-lg p-6 w-full max-w-md text-left space-y-3">
                  <h2 className="font-semibold text-foreground text-lg">
                    Message from the Test Creators
                  </h2>
                  <div 
                    className="text-sm text-muted-foreground prose prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: state.employerMessage }}
                  />
                </div>
              </>
            )}

            {/* Action Button */}
            <Button
              onClick={() => navigate("/dashboard")}
              size="lg"
              className="mt-4"
            >
              Back to Dashboard
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TestSubmissionSuccess;
